<template>
  <component
    :is="icon"
    :class="mergedClasses"
    v-bind="$attrs"
  />
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { LUCIDE_ICON_MAP, type IconName } from './lucideMapping'

type ClassValue = string | string[] | Record<string, boolean | undefined> | undefined

export interface Props {
  name: IconName
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | number
  color?: string
  class?: ClassValue
}

const props = withDefaults(defineProps<Props>(), {
  size: 'md',
  color: 'currentColor'
})

// Define size mappings
const sizeMap: Record<string, string> = {
  xs: 'w-2.5 h-2.5',
  sm: 'w-3.5 h-3.5',
  md: 'w-4 h-4',
  lg: 'w-5 h-5',
  xl: 'w-6 h-6',
  xxl: 'w-8 h-8'
}

// Get icon component
const icon = computed(() => {
  const iconComponent = LUCIDE_ICON_MAP[props.name]
  if (!iconComponent) {
    console.warn(`Icon "${props.name}" not found in mapping`)
    return LUCIDE_ICON_MAP.info
  }
  return iconComponent
})

// Compute size class
const sizeClass = computed(() => {
  if (typeof props.size === 'number') {
    return `w-[${props.size}px] h-[${props.size}px]`
  }
  return sizeMap[props.size] || sizeMap.md
})

// Compute color class
const colorClass = computed(() => {
  if (props.color === 'currentColor') {
    return ''
  }
  return `text-${props.color}`
})

// Merge all classes
const mergedClasses = computed(() => {
  const baseClasses = [sizeClass.value, colorClass.value]
  
  if (!props.class) {
    return baseClasses
  }
  
  // Handle string, array, or object classes
  if (typeof props.class === 'string') {
    return [...baseClasses, props.class]
  } else if (Array.isArray(props.class)) {
    return [...baseClasses, ...props.class]
  } else if (typeof props.class === 'object') {
    return [...baseClasses, props.class]
  }
  
  return baseClasses
})

// Fallback for safety
defineOptions({
  inheritAttrs: false
})
</script>
